import { useMaintenance } from '@/maintenance/store';
import { isMaintenanceResponse } from '@/maintenance/response';
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { clearAllOfflineData, db, getUnsyncedSummary, type UnsyncedSummary } from '@/offline/db';
import { AccessDeniedScreen } from './AccessDeniedScreen';
import { LogoutConfirmDialog } from './LogoutConfirmDialog';
import { portalLogout } from './portalLogout';
import { AuthUser, hasPermission, isViewerOnly } from './permissions';
import { isPublicPath } from '@/lib/publicPaths';
import { onSessionExpired } from './sessionExpiry';

// Portal login URL
const PORTAL_LOGIN_URL = 'https://nadc1.net/login';

/** The portal login, returning to where the SPA is now.
 *  ALWAYS build the return target from the SPA's own location — the
 *  server's `redirect` hint captured the API URL that 401'd (e.g.
 *  /api/v1/me), so following it lands the user on raw JSON after
 *  login (live bug, 2026-07-23). */
function portalLoginHref(): string {
  return `${PORTAL_LOGIN_URL}?redirect=${encodeURIComponent(window.location.href)}`;
}

/**
 * Public routes skip the session probe entirely — see src/lib/publicPaths.ts
 * for why the list lives there and what else consumes it. Without this, the
 * bootstrap's 401 handler window.location.replace()s a public visitor to the
 * portal login, which nothing downstream can veto. That silently broke the week
 * link (/my-schedule/:item/:token) for every field employee — unnoticed only
 * because carrier delivery has been blocked (Twilio 30032) since it shipped.
 */

export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isRedirecting: boolean;
  accessDenied: boolean;
  /** Whether the user holds a backend permission name, e.g. can('projects.create'). */
  can: (permission: string) => boolean;
  /** True when the user's only role is the default read-only 'viewer' role. */
  isReadOnly: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const maintenance = useMaintenance();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [logoutPrompt, setLogoutPrompt] = useState<UnsyncedSummary | null>(null);

  // Check portal session on mount
  useEffect(() => {
    // This bootstrap can still be in flight when the provider goes away —
    // StrictMode double-invokes effects, and tests unmount mid-request. A late
    // resolution then calls setState against a torn-down tree, which surfaced
    // as intermittent "window is not defined" unhandled rejections in the suite
    // (~9 per affected run, from the IndexedDB path below).
    //
    // This cannot swallow a real redirect: AuthProvider is mounted at the app
    // root and never unmounts before the redirect it is guarding.
    let cancelled = false;
    if (maintenance) return;

    // Public route: never probe the session, never redirect. Drop straight to
    // "not loading, not authenticated" and let the route render.
    if (isPublicPath(window.location.pathname)) {
      setIsLoading(false);
      return;
    }

    /** Hand off to the portal to sign in (see portalLoginHref). */
    function redirectToPortalLogin() {
      if (cancelled) return;
      setIsRedirecting(true);
      window.location.replace(portalLoginHref());
      // Don't set isLoading=false - keep showing loading while redirecting
    }

    async function checkPortalSession() {
      try {
        // Try to get user info from API (uses portal session cookie)
        const response = await apiClient.get('/api/v1/me');

        if (response.data?.data) {
          const nextUser = response.data.data as AuthUser;

          // BUGS #21 identity check: a DIFFERENT portal user on this device
          // must never inherit the previous user's queue or photo blobs —
          // clear BEFORE their session begins. (Same user resuming keeps the
          // queue; the Sync Center / Today badges surface it.)
          const prior = await db.session.get('current');
          const priorId = (prior?.user as { id?: string | number } | undefined)?.id;
          if (priorId !== undefined && String(priorId) !== String(nextUser.id)) {
            const { items, blobs } = await getUnsyncedSummary();
            if (items > 0 || blobs > 0) {
              console.warn(`[auth] different user signed in — clearing ${items} queued item(s) and ${blobs} attachment blob(s) left by the previous user`);
            }
            await clearAllOfflineData();
          }

          if (cancelled) return;
          setUser(nextUser);

          // Cache user in IndexedDB for offline access
          await db.session.put({
            id: 'current',
            token: 'portal-session', // Placeholder - actual auth is cookie-based
            user: nextUser,
            expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(), // 8 hours
          });
          if (cancelled) return;
          setIsLoading(false);
        }
      } catch (error) {
        if (isMaintenanceResponse((error as { response?: { status?: number; headers?: unknown } }).response)) return;
        const err = error as { response?: { status?: number; data?: { redirect?: string } } };
        console.log('Portal session check failed:', err?.response?.status);

        // 403 = logged into the portal but NOT granted the DemoOps app.
        // Redirecting to the portal login would bounce straight back here
        // (SSO auto-redirect) and loop forever — show an explanation instead.
        if (err?.response?.status === 403) {
          if (cancelled) return;
          setAccessDenied(true);
          setIsLoading(false);
          return;
        }

        // 401 = no portal session: redirect to portal login.
        if (err?.response?.status === 401) {
          redirectToPortalLogin();
          return;
        }

        // For network errors, try cached session for offline access
        try {
          const session = await db.session.get('current');
          if (cancelled) return;
          if (session?.user) {
            console.log('Using cached session for offline access');
            setUser(session.user as AuthUser);
          } else {
            // No cached session and can't reach API - redirect to portal
            redirectToPortalLogin();
            return;
          }
        } catch {
          // IndexedDB error - redirect to portal
          redirectToPortalLogin();
          return;
        }
        if (cancelled) return;
        setIsLoading(false);
      }
    }

    checkPortalSession();

    return () => {
      cancelled = true;
    };
  }, [maintenance]);

  // Mid-session expiry (sweep 2026-09-15). The portal session is an absolute
  // 8 h; when it ends under an open tab every /api/v1 call 401s, and nothing
  // but a hard reload used to notice. The API client reports each 401
  // (sessionExpiry.ts); the FIRST one after sign-in signs this tab out —
  // isAuthenticated=false stops every gated poller and ProtectedRoute unmounts
  // the page, which stops the rest — cancels what is in flight, and hands off
  // to the portal with the page to come back to. Once: poll ticks that slip
  // out before the page unloads change nothing. A 401 before sign-in belongs
  // to the bootstrap above. Offline data is NOT cleared — expiry is not a
  // logout; the same user gets their queue back after signing in.
  const userRef = useRef<AuthUser | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);
  const expiredRef = useRef(false);
  useEffect(() => onSessionExpired(() => {
    if (!userRef.current || expiredRef.current) return;
    expiredRef.current = true;
    setIsRedirecting(true);
    setUser(null);
    void queryClient.cancelQueries();
    window.location.replace(portalLoginHref());
  }), [queryClient]);

  /** The destructive half: wipe this device's offline data, then leave.
   *  ONLY explicit user logout reaches here — session expiry (401) never
   *  clears the queue (same user keeps their unsynced work for next login). */
  const performLogout = useCallback(async () => {
    setUser(null);
    setLogoutPrompt(null);
    // Every Dexie store incl. session, outbox, and attachment blobs
    // (settings/device_id intentionally kept — see clearAllOfflineData).
    await clearAllOfflineData();
    queryClient.clear();
    portalLogout();
  }, [queryClient]);

  /** Guarded logout: unsynced work blocks with a plain-words confirm. */
  const logout = useCallback(async () => {
    const summary = await getUnsyncedSummary();
    if (summary.items === 0 && summary.blobs === 0) {
      await performLogout();
      return;
    }
    setLogoutPrompt(summary);
  }, [performLogout]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/me');

      if (response.data?.data) {
        setUser(response.data.data);

        // Update cached user data
        const session = await db.session.get('current');
        if (session) {
          await db.session.put({
            ...session,
            user: response.data.data,
          });
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const can = useCallback((permission: string) => hasPermission(user, permission), [user]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isRedirecting,
    accessDenied,
    can,
    isReadOnly: isViewerOnly(user),
    logout,
    refreshUser,
  };

  if (accessDenied) {
    return <AccessDeniedScreen />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {logoutPrompt && (
        <LogoutConfirmDialog
          summary={logoutPrompt}
          onStay={() => setLogoutPrompt(null)}
          onDelete={performLogout}
          onSynced={async () => {
            const s = await getUnsyncedSummary();
            if (s.items === 0) await performLogout();       // drained — leftover blobs get wiped anyway
            else setLogoutPrompt(s);                        // best effort failed: refreshed counts, stay open
          }}
        />
      )}
    </AuthContext.Provider>
  );
}
